// src/pages/AboutUs.jsx
import React from "react";
import { motion } from "framer-motion";

// Framer Motion variant for content blocks to fade in and slide up
const itemVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const AboutUs = () => {
  return (
    // Main container with dark background, padding, and centered content
    <section className="bg-gray-900 text-white py-20 px-6 md:px-8 lg:px-12 max-w-7xl mx-auto min-h-screen">
      
      {/* ---------------------------------------------------- */}
      {/* HEADER SECTION: Tagline & Initial Hook (Animated) */}
      {/* ---------------------------------------------------- */}
      <motion.h2
        className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 leading-tight relative"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        Web3 Innovation Without The BS 🚀
        {/* Decorative Underline (Animated) */}
        <span className="absolute bottom-[-10px] left-0 w-24 h-1 bg-gradient-to-r from-green-500 to-teal-400" />
      </motion.h2>

      <motion.p
        className="text-lg md:text-xl text-gray-200 mb-16 max-w-4xl leading-relaxed tracking-wide"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        MosaicTech is a full-stack Web3 powerhouse. We handle everything from
        smart contracts, NFT minting, and Solana trading bots, to meme launches,
        raid management, and marketing. Straight to the point, no fluff — if
        it’s Web3, you’re in the right place.
      </motion.p>
      
      {/* ---------------------------------------------------- */}
      {/* CONTENT GRID: Mission and Services */}
      {/* ---------------------------------------------------- */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        
        {/* BLOCK 1: Our Core Pledge (Animated on scroll) */}
        <motion.div 
          className="p-8 bg-gray-800 rounded-xl shadow-2xl border border-gray-700 hover:border-teal-400 transition-all duration-300"
          variants={itemVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }} // Trigger animation when 40% of the element is visible
        >
          <h3 className="text-3xl font-bold text-teal-400 mb-4 flex items-center">
            <span className="mr-3">⚙️</span> Our Core Pledge
          </h3>
          <p className="text-gray-300 leading-7">
            We are the engine room for the next generation of decentralized projects. 
            Our focus is on delivering secure, scalable, and verifiable results, 
            cutting through the hype to provide real utility. We build on Ethereum, 
            Solana, and emerging Layer-2s, treating **code as the ultimate truth.**
          </p>
        </motion.div>

        {/* BLOCK 2: Full-Stack Web3 Powerhouse (Animated on scroll with delay) */}
        <motion.div 
          className="p-8 bg-gray-800 rounded-xl shadow-2xl border border-gray-700 hover:border-green-500 transition-all duration-300"
          variants={itemVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          transition={{ delay: 0.1 }} // Small delay for stagger effect
        >
          <h3 className="text-3xl font-bold text-green-500 mb-4 flex items-center">
            <span className="mr-3">🛠️</span> Full-Stack Power
          </h3>
          <p className="text-gray-300 leading-7">
            From concept to launch, we cover the entire spectrum. Our expertise spans 
            **Smart Contract Development** (security audits, token standards), 
            **Digital Asset Deployment** (NFT minting platforms, custom marketplaces), 
            **Decentralized Finance (DeFi) Tools**, and **Community Management** (raid strategies, viral marketing).
          </p>
        </motion.div>

      </div>
      
      {/* ---------------------------------------------------- */}
      {/* CALL TO ACTION SECTION: Team and Transparency (Animated on scroll) */}
      {/* ---------------------------------------------------- */}
      <motion.div 
        className="mt-20 p-10 bg-gray-800 rounded-2xl border-2 border-green-500/50 text-center"
        variants={itemVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        transition={{ delay: 0.2 }}
      >
        <h3 className="text-4xl font-extrabold text-white mb-4">
          Meet The Architects of MosaicTech
        </h3>
        <p className="text-xl text-gray-300 mb-8 max-w-5xl mx-auto">
          MosaicTech isn't just code; it's a collective of seasoned developers, 
          blockchain architects, and community strategists. We operate with 
          **radical transparency**.
        </p>
        
        <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
          <a 
            href="/team" 
            className="px-8 py-3 text-lg font-semibold rounded-full bg-teal-500 text-white hover:bg-teal-400 transition-colors shadow-lg shadow-teal-500/30"
          >
            Explore Our Team
          </a>
          <a 
            href="/portfolio" 
            className="px-8 py-3 text-lg font-semibold rounded-full border border-gray-500 text-gray-200 hover:border-white hover:text-white transition-colors"
          >
            View Our Portfolio
          </a>
        </div>
      </motion.div>

    </section>
  );
};

export default AboutUs;